/**
 * Network export overlay builder (PRD-05b FR2/FR3).
 *
 * Turns the Fixed Routes drawer's edits into the **overlay** the server merges with a
 * hosted feed for a full edited-network TransXChange export. The overlay is:
 *   - a **GTFS slice** of the edited + new routes (reusing `buildGtfsFeed` — the same
 *     trip synthesis / calendar dedup / id resolution the BODS export already relies on),
 *   - the set of **replaced** `gtfsRouteId`s (forks of THIS feed → swap their store original),
 *   - the set of **removed** `gtfsRouteId`s (store routes to drop — passed in; no UI yet),
 *   - a **stop_id → ATCO** map for stops that carry a NaPTAN code, so forked stops export a
 *     real `StopPointRef` (store-origin stops already use the ATCO as their stop_id).
 *
 * "Edited/new" = a route forked from this feed (`networkOrigin.feedId === feedId`, a
 * replacement) or a brand-new authored route (no `networkOrigin`, appended). Forks of a
 * DIFFERENT feed are excluded — they belong to another network. POSTed at export time and
 * discarded server-side; never persisted.
 */

import type { FixedRoute, FixedRoutesState, FixedRouteStop, ServiceDaysMask } from '../../types/fixedRoutes';
import { buildGtfsFeed, type GtfsExportFeed, type SkippedRoute } from './gtfsExporter';

/** Heuristic match for a NaPTAN ATCO code (3-digit area + alphanum) — mirrors the writer. */
const looksLikeAtco = (id: string): boolean => /^[0-9]{3}[0-9A-Za-z]{5,9}$/.test(id);

/** A forked route's id: `route-net-{gtfsRouteId}-{forkedAtMs}` (set by networkRouteToFixedRoute).
 * Lets us recover the hosted route_id even if networkOrigin/gtfsRouteId were stripped. */
const FORK_ID_RE = /^route-net-(.+)-\d+$/;

/** Recover the GTFS stop_id `buildGtfsFeed` assigns to a stop (matches bodsExporter). */
const gtfsStopIdBase = (s: FixedRouteStop): string =>
  s.uatStopId?.trim()
  || (s.id.startsWith('gtfs-stop-') ? s.id.slice('gtfs-stop-'.length) : s.id);

export interface NetworkExportOverlay {
  feed: GtfsExportFeed;
  replacedGtfsRouteIds: string[];
  removedGtfsRouteIds: string[];
  atcoByStopId: Record<string, string>;
}

export interface BuildOverlayOptions {
  defaultServiceDays: ServiceDaysMask;
  defaultServiceDateRange: { startDate: string; endDate: string };
  defaultRouteType?: number;
  meta: Parameters<typeof buildGtfsFeed>[1]['meta'];
  /** Store routes the user removed (dropped from the export). No UI yet → defaults to []. */
  removedGtfsRouteIds?: string[];
}

export interface NetworkExportOverlayResult {
  overlay: NetworkExportOverlay;
  /** Forked-from-this-feed routes that replace their store original. */
  editedCount: number;
  /** Brand-new authored routes appended to the network. */
  newCount: number;
  /** Overlay stops exported without a NaPTAN ATCO (from-scratch) — surfaced as a warning. */
  stopsWithoutAtco: number;
  warnings: string[];
  skippedRoutes: SkippedRoute[];
}

export function buildNetworkExportOverlay(
  state: FixedRoutesState,
  feedId: string,
  options: BuildOverlayOptions,
): NetworkExportOverlayResult {
  // Recover the hosted route_id this drawer route edits — robust to `networkOrigin` being
  // dropped by a save/load or layer roundtrip. Order: networkOrigin → top-level gtfsRouteId
  // → the fork id pattern `route-net-{gtfsRouteId}-{ts}` (the id always survives). This is
  // the id the server matches against the feed's routes to decide replace-vs-append, so it
  // must not depend on the (fragile) networkOrigin.feedId matching the export feed.
  const hostedRouteId = (r: FixedRoute): string | undefined =>
    r.networkOrigin?.gtfsRouteId || r.gtfsRouteId || FORK_ID_RE.exec(r.id)?.[1] || undefined;

  // Every drawer route is part of the working set: a forked route (has a hosted route_id) is
  // an edit to merge; a drawn route (none) is new. Patch the recovered id onto gtfsRouteId so
  // buildGtfsFeed emits the overlay route under the hosted route_id → the server replaces the
  // matching store route. (Previously this required networkOrigin.feedId to exactly equal the
  // export feed, which silently dropped edits after an export→re-import→re-fork loop.)
  const patchedRoutes: FixedRoute[] = state.routes.map((r) => {
    const g = hostedRouteId(r);
    return g && !r.gtfsRouteId ? { ...r, gtfsRouteId: g } : r;
  });

  const result = buildGtfsFeed({ ...state, routes: patchedRoutes }, {
    selectedRouteIds: patchedRoutes.map((r) => r.id),
    defaultServiceDays: options.defaultServiceDays,
    defaultServiceDateRange: options.defaultServiceDateRange,
    defaultRouteType: options.defaultRouteType,
    meta: options.meta,
  });

  const replacedGtfsRouteIds = patchedRoutes
    .map(hostedRouteId)
    .filter((g): g is string => !!g);

  // stop_id → ATCO for stops that carry one (forked BODS stops; the rest export synthetic).
  const atcoByStopId: Record<string, string> = {};
  for (const s of state.stops) {
    if (s.atcoCode?.trim()) atcoByStopId[gtfsStopIdBase(s)] = s.atcoCode.trim();
  }

  // Pre-flight count of from-scratch stops in the slice (no ATCO + not ATCO-shaped).
  let stopsWithoutAtco = 0;
  for (const st of result.feed.stops) {
    const hasAtco = !!atcoByStopId[st.stop_id] || looksLikeAtco(st.stop_id);
    if (!hasAtco) stopsWithoutAtco += 1;
  }

  return {
    overlay: {
      feed: result.feed,
      replacedGtfsRouteIds,
      removedGtfsRouteIds: options.removedGtfsRouteIds ?? [],
      atcoByStopId,
    },
    editedCount: replacedGtfsRouteIds.length,
    newCount: patchedRoutes.length - replacedGtfsRouteIds.length,
    stopsWithoutAtco,
    warnings: result.warnings,
    skippedRoutes: result.skippedRoutes,
  };
}
